import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import fundReturnProvider, {
	type BlockedWallet,
	type BroadcastFundReturnRequest,
	type CreateFundReturnRequest,
	type FundReturn,
} from "src/providers/fund-return-provider";

export function useBlockedWallets() {
	return useQuery<BlockedWallet[]>({
		queryKey: ["blocked-wallets"],
		queryFn: () => fundReturnProvider.listBlockedWallets(),
		staleTime: 30_000,
		refetchInterval: 60_000,
	});
}

export function useFundReturns(
	params: { limit?: number; offset?: number; status?: string } = {},
) {
	return useQuery<{ fundReturns: FundReturn[]; total: number }>({
		queryKey: ["fund-returns", params],
		queryFn: () => fundReturnProvider.listFundReturns(params),
		staleTime: 30_000,
		refetchInterval: 60_000,
	});
}

export function useFundReturn(id: number | null) {
	return useQuery<FundReturn>({
		queryKey: ["fund-return", id],
		queryFn: () => {
			if (id === null) {
				return Promise.reject(new Error("Fund return ID is required"));
			}
			return fundReturnProvider.getFundReturn(id);
		},
		enabled: id !== null,
		staleTime: 30_000,
	});
}

export function useCreateFundReturn() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateFundReturnRequest) =>
			fundReturnProvider.createFundReturn(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fund-returns"] });
			queryClient.invalidateQueries({ queryKey: ["blocked-wallets"] });
		},
	});
}

export function useBroadcastFundReturn() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			data,
		}: {
			id: number;
			data: BroadcastFundReturnRequest;
		}) => fundReturnProvider.broadcastFundReturn(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["fund-returns"] });
			queryClient.invalidateQueries({ queryKey: ["blocked-wallets"] });
		},
	});
}
